/**
 * AST Operations System
 * Defines atomic operations for AST manipulation and undo/redo
 */

import { AstSelection, Block, BlockAttrs, ContentAST, InlineContent, ListItem, Mark } from '@shared/ast/astTypes'

// Operation types for different AST modifications
export type AstOperation =
  | InsertTextOperation
  | DeleteTextOperation
  | InsertBlockOperation
  | DeleteBlockOperation
  | SplitBlockOperation
  | MergeBlocksOperation
  | SetMarkOperation
  | RemoveMarkOperation
  | SetBlockAttrsOperation
  | InsertListItemOperation
  | DeleteListItemOperation
  | IndentListItemOperation
  | UnindentListItemOperation
  | MoveBlockOperation
  | StateChangeOperation
  | WrapEncryptedOperation
  | UnwrapEncryptedOperation

// Base operation interface
export interface BaseOperation {
  type: string
  timestamp: number
  blockId: string
}

// Text operations
export interface InsertTextOperation extends BaseOperation {
  type: 'insert_text'
  offset: number
  text: string
  marks?: Mark[]
}

export interface DeleteTextOperation extends BaseOperation {
  type: 'delete_text'
  offset: number
  length: number
  deletedText?: string // For undo
  deletedMarks?: Mark[][] // Marks for each deleted character
}

// Block operations
export interface InsertBlockOperation extends BaseOperation {
  type: 'insert_block'
  block: Block
  index: number
}

export interface DeleteBlockOperation extends BaseOperation {
  type: 'delete_block'
  index: number
  deletedBlock?: Block // For undo
}

export interface SplitBlockOperation extends BaseOperation {
  type: 'split_block'
  offset: number
  newBlockId: string
}

export interface MergeBlocksOperation extends BaseOperation {
  type: 'merge_blocks'
  targetBlockId: string
  mergedContent?: InlineContent[] // For undo
}

// Formatting operations
export interface SetMarkOperation extends BaseOperation {
  type: 'set_mark'
  offset: number
  length: number
  mark: Mark
}

export interface RemoveMarkOperation extends BaseOperation {
  type: 'remove_mark'
  offset: number
  length: number
  mark: Mark
}

// Block attribute operations
export interface SetBlockAttrsOperation extends BaseOperation {
  type: 'set_block_attrs'
  attrs: BlockAttrs | undefined
  previousAttrs?: BlockAttrs // For undo
}

// List operations
export interface InsertListItemOperation extends BaseOperation {
  type: 'insert_list_item'
  item: ListItem
  index: number
  parentItemId?: string // For nested items
}

export interface DeleteListItemOperation extends BaseOperation {
  type: 'delete_list_item'
  index: number
  parentItemId?: string
  deletedItem?: ListItem // For undo
}

export interface IndentListItemOperation extends BaseOperation {
  type: 'indent_list_item'
  itemId: string
  parentListId: string
  previousParentItemId?: string // For undo
  newParentItemId?: string
}

export interface UnindentListItemOperation extends BaseOperation {
  type: 'unindent_list_item'
  itemId: string
  parentListId: string
  previousParentItemId?: string // For undo
  newParentItemId?: string
}

// Block movement operations
export interface MoveBlockOperation extends BaseOperation {
  type: 'move_block'
  fromIndex: number
  toIndex: number
}

// State-based operation (replaces entire AST state)
export interface StateChangeOperation extends BaseOperation {
  type: 'state_change'
  data: {
    previousAST: ContentAST
    newAST: ContentAST
    previousSelection: AstSelection
    newSelection: AstSelection
  }
}

// Encryption operations
export interface WrapEncryptedOperation extends BaseOperation {
  type: 'wrap_encrypted'
  offset: number
  length: number
  encryptionId: string
  wrappedContent?: InlineContent[] // For undo
}

export interface UnwrapEncryptedOperation extends BaseOperation {
  type: 'unwrap_encrypted'
  offset: number
  encryptionId: string
  unwrappedContent?: InlineContent[] // For undo - the entire encrypted node
}

// Transaction wraps multiple operations that should be undone/redone together
export interface AstTransaction {
  id: string
  operations: AstOperation[]
  inverseOperations: AstOperation[]
  selectionBefore: AstSelection
  selectionAfter: AstSelection
  reason: string
  timestamp: number
  coalesceKey?: string // For operation coalescing
  editorStateBefore?: unknown // Opaque Lexical EditorState for precise undo restoration
  editorStateAfter?: unknown // Opaque Lexical EditorState for precise redo restoration
}

// Coalescing configuration
export interface CoalescingConfig {
  enabled: boolean
  maxTextInsertions: number // Max consecutive text insertions to coalesce
  maxTime: number // Max time between operations to coalesce (ms)
  breakOnSelectionJump: boolean // Stop coalescing if selection jumps significantly
  breakOnStructuralChange: boolean // Stop coalescing on block-level changes
}

// Default coalescing configuration
export const defaultCoalescingConfig: CoalescingConfig = {
  enabled: true,
  maxTextInsertions: 50,
  maxTime: 1000, // 1 second
  breakOnSelectionJump: true,
  breakOnStructuralChange: true,
}

// Operation factory functions
export const createInsertTextOperation = (
  blockId: string,
  offset: number,
  text: string,
  marks?: Mark[],
): InsertTextOperation => ({
  type: 'insert_text',
  timestamp: Date.now(),
  blockId,
  offset,
  text,
  marks,
})

export const createDeleteTextOperation = (
  blockId: string,
  offset: number,
  length: number,
  deletedText?: string,
  deletedMarks?: Mark[][],
): DeleteTextOperation => ({
  type: 'delete_text',
  timestamp: Date.now(),
  blockId,
  offset,
  length,
  deletedText,
  deletedMarks,
})

export const createInsertBlockOperation = (blockId: string, block: Block, index: number): InsertBlockOperation => ({
  type: 'insert_block',
  timestamp: Date.now(),
  blockId,
  block,
  index,
})

export const createDeleteBlockOperation = (
  blockId: string,
  index: number,
  deletedBlock?: Block,
): DeleteBlockOperation => ({
  type: 'delete_block',
  timestamp: Date.now(),
  blockId,
  index,
  deletedBlock,
})

export const createSplitBlockOperation = (
  blockId: string,
  offset: number,
  newBlockId: string,
): SplitBlockOperation => ({
  type: 'split_block',
  timestamp: Date.now(),
  blockId,
  offset,
  newBlockId,
})

export const createMergeBlocksOperation = (
  blockId: string,
  targetBlockId: string,
  mergedContent?: InlineContent[],
): MergeBlocksOperation => ({
  type: 'merge_blocks',
  timestamp: Date.now(),
  blockId,
  targetBlockId,
  mergedContent,
})

export const createSetMarkOperation = (
  blockId: string,
  offset: number,
  length: number,
  mark: Mark,
): SetMarkOperation => ({
  type: 'set_mark',
  timestamp: Date.now(),
  blockId,
  offset,
  length,
  mark,
})

export const createRemoveMarkOperation = (
  blockId: string,
  offset: number,
  length: number,
  mark: Mark,
): RemoveMarkOperation => ({
  type: 'remove_mark',
  timestamp: Date.now(),
  blockId,
  offset,
  length,
  mark,
})

export const createSetBlockAttrsOperation = (
  blockId: string,
  attrs: BlockAttrs | undefined,
  previousAttrs?: BlockAttrs,
): SetBlockAttrsOperation => ({
  type: 'set_block_attrs',
  timestamp: Date.now(),
  blockId,
  attrs,
  previousAttrs,
})

export const createIndentListItemOperation = (
  itemId: string,
  parentListId: string,
  previousParentItemId?: string,
  newParentItemId?: string,
): IndentListItemOperation => ({
  type: 'indent_list_item',
  timestamp: Date.now(),
  blockId: parentListId,
  itemId,
  parentListId,
  previousParentItemId,
  newParentItemId,
})

export const createUnindentListItemOperation = (
  itemId: string,
  parentListId: string,
  previousParentItemId?: string,
  newParentItemId?: string,
): UnindentListItemOperation => ({
  type: 'unindent_list_item',
  timestamp: Date.now(),
  blockId: parentListId,
  itemId,
  parentListId,
  previousParentItemId,
  newParentItemId,
})

export const createWrapEncryptedOperation = (
  blockId: string,
  offset: number,
  length: number,
  encryptionId: string,
  wrappedContent?: InlineContent[],
): WrapEncryptedOperation => ({
  type: 'wrap_encrypted',
  timestamp: Date.now(),
  blockId,
  offset,
  length,
  encryptionId,
  wrappedContent,
})

export const createUnwrapEncryptedOperation = (
  blockId: string,
  offset: number,
  encryptionId: string,
  unwrappedContent?: InlineContent[],
): UnwrapEncryptedOperation => ({
  type: 'unwrap_encrypted',
  timestamp: Date.now(),
  blockId,
  offset,
  encryptionId,
  unwrappedContent,
})

// Operation validation
export const validateOperation = (operation: AstOperation): boolean => {
  if (!operation.blockId || !operation.type || !operation.timestamp) {
    return false
  }

  switch (operation.type) {
    case 'insert_text':
    case 'delete_text':
      return typeof operation.offset === 'number' && operation.offset >= 0

    case 'insert_block':
    case 'delete_block':
      return typeof operation.index === 'number' && operation.index >= 0

    case 'set_mark':
    case 'remove_mark':
      return (
        typeof operation.offset === 'number' &&
        operation.offset >= 0 &&
        typeof operation.length === 'number' &&
        operation.length > 0
      )

    case 'state_change': {
      const stateOp = operation as StateChangeOperation
      return !!(stateOp.data && stateOp.data.newAST && stateOp.data.previousAST)
    }

    case 'indent_list_item':
    case 'unindent_list_item': {
      const listOp = operation as IndentListItemOperation | UnindentListItemOperation
      return !!(listOp.itemId && listOp.parentListId)
    }

    case 'wrap_encrypted': {
      const wrapOp = operation as WrapEncryptedOperation
      return !!(wrapOp.offset >= 0 && wrapOp.length > 0 && wrapOp.encryptionId)
    }

    case 'unwrap_encrypted': {
      const unwrapOp = operation as UnwrapEncryptedOperation
      return !!(unwrapOp.offset >= 0 && unwrapOp.encryptionId)
    }

    default:
      return true
  }
}

// Generates inverse operation for undo
export const createInverseOperation = (operation: AstOperation): AstOperation => {
  switch (operation.type) {
    case 'insert_text':
      return createDeleteTextOperation(operation.blockId, operation.offset, operation.text.length)

    case 'delete_text':
      return createInsertTextOperation(
        operation.blockId,
        operation.offset,
        operation.deletedText || '',
        operation.deletedMarks?.[0],
      )

    case 'insert_block':
      return createDeleteBlockOperation(operation.blockId, operation.index)

    case 'delete_block':
      if (!operation.deletedBlock) {
        throw new Error('Cannot create inverse for delete_block without deletedBlock')
      }
      return createInsertBlockOperation(operation.blockId, operation.deletedBlock, operation.index)

    case 'split_block':
      return createMergeBlocksOperation(operation.blockId, operation.newBlockId)

    case 'merge_blocks':
      return createSplitBlockOperation(
        operation.blockId,
        0, // This would need more context in a real implementation
        operation.targetBlockId,
      )

    case 'set_mark':
      return createRemoveMarkOperation(operation.blockId, operation.offset, operation.length, operation.mark)

    case 'remove_mark':
      return createSetMarkOperation(operation.blockId, operation.offset, operation.length, operation.mark)

    case 'state_change': {
      // For state change operations, the inverse is already provided in the data
      const stateOp = operation as StateChangeOperation
      return {
        type: 'state_change',
        blockId: stateOp.blockId,
        data: {
          previousAST: stateOp.data.newAST,
          newAST: stateOp.data.previousAST,
          previousSelection: stateOp.data.newSelection,
          newSelection: stateOp.data.previousSelection,
        },
        timestamp: Date.now(),
      } as StateChangeOperation
    }

    case 'indent_list_item': {
      const indentOp = operation as IndentListItemOperation
      return createUnindentListItemOperation(
        indentOp.itemId,
        indentOp.parentListId,
        indentOp.newParentItemId,
        indentOp.previousParentItemId,
      )
    }

    case 'unindent_list_item': {
      const unindentOp = operation as UnindentListItemOperation
      return createIndentListItemOperation(
        unindentOp.itemId,
        unindentOp.parentListId,
        unindentOp.newParentItemId,
        unindentOp.previousParentItemId,
      )
    }

    case 'set_block_attrs': {
      const setAttrsOp = operation as SetBlockAttrsOperation
      return createSetBlockAttrsOperation(setAttrsOp.blockId, setAttrsOp.previousAttrs, setAttrsOp.attrs)
    }

    case 'wrap_encrypted': {
      const wrapOp = operation as WrapEncryptedOperation
      return createUnwrapEncryptedOperation(wrapOp.blockId, wrapOp.offset, wrapOp.encryptionId, wrapOp.wrappedContent)
    }

    case 'unwrap_encrypted': {
      const unwrapOp = operation as UnwrapEncryptedOperation
      if (!unwrapOp.unwrappedContent) {
        throw new Error('Cannot create inverse for unwrap_encrypted without unwrappedContent')
      }
      // Calculate length from the unwrapped content
      const length = unwrapOp.unwrappedContent.reduce((sum, node) => {
        if (node.type === 'text') {
          return sum + node.text.length
        }
        if (node.type === 'encrypted') {
          // Calculate length of encrypted node content
          return sum + node.content.reduce((s, n) => s + (n.type === 'text' ? n.text.length : 0), 0)
        }
        return sum
      }, 0)
      return createWrapEncryptedOperation(unwrapOp.blockId, unwrapOp.offset, length, unwrapOp.encryptionId)
    }

    default:
      throw new Error(`Cannot create inverse for operation type: ${operation.type}`)
  }
}

// Checks if two operations can be coalesced
export const canCoalesceOperations = (op1: AstOperation, op2: AstOperation, config: CoalescingConfig): boolean => {
  if (!config.enabled) {return false}

  // Must be same block and operation type
  if (op1.blockId !== op2.blockId || op1.type !== op2.type) {
    return false
  }

  // Check time window
  if (op2.timestamp - op1.timestamp > config.maxTime) {
    return false
  }

  // Only coalesce text insertions for now
  if (op1.type === 'insert_text' && op2.type === 'insert_text') {
    const insertOp1 = op1 as InsertTextOperation
    const insertOp2 = op2 as InsertTextOperation

    // Must be consecutive positions
    return insertOp2.offset === insertOp1.offset + insertOp1.text.length
  }

  return false
}

// Coalesces two operations into one
export const coalesceOperations = (op1: AstOperation, op2: AstOperation): AstOperation => {
  if (op1.type === 'insert_text' && op2.type === 'insert_text') {
    const insertOp1 = op1 as InsertTextOperation
    const insertOp2 = op2 as InsertTextOperation

    return createInsertTextOperation(
      insertOp1.blockId,
      insertOp1.offset,
      insertOp1.text + insertOp2.text,
      insertOp1.marks, // Use marks from first operation
    )
  }

  throw new Error('Cannot coalesce incompatible operations')
}
